import React from 'react';
import type { BCPSectionConfig } from './bcpFieldConfig';

export const INTRODUCTORY_BCP_SECTIONS: BCPSectionConfig[] = [
  {
    id: 'basic_info',
    title: '1. 事業者情報・基本方針',
    fields: [
      {
        id: 'company_info',
        label: '1-1. 事業者名',
        type: 'input',
        placeholder: '清島商事株式会社',
        aiConsult: false,
      },
      {
        id: 'business_continuity',
        label: '1-2. 所在地',
        type: 'input',
        placeholder: '〒000-0000 東京都〇〇区〇〇1-1-1',
        aiConsult: false,
      },
      {
        id: 'basic_policy',
        label: '1-3. 基本方針（首都直下地震発生時は以下の方針に則り行動する）',
        type: 'textarea',
        minHeight: 'small',
        aiConsult: true,
        defaultValue:
          '①従業員とその家族の安全を守る\n②現在の事業規模を維持し、従業員の雇用を守る\n③事業を早期復旧し、顧客の信用を守る\n④帰宅困難者や地域住民を支援し、地域社会に貢献する',
        placeholder:
          '①従業員とその家族の安全を守る\n②現在の事業規模を維持し、従業員の雇用を守る\n③事業を早期復旧し、顧客の信用を守る\n④帰宅困難者や地域住民を支援し、地域社会に貢献する',
      },
    ],
  },
  {
    id: 'crisis_management',
    title: '2. 想定リスク',
    fields: [
      {
        id: 'expected_damage',
        label: '2-1. 所在地で想定される地震被害',
        type: 'textarea',
        minHeight: 'small',
        aiConsult: true,
        defaultValue: '地震：リスク（震度6強）\n液状化：リスク低\n土砂災害：リスク低',
        guide: {
          content: (
            <div className="space-y-2">
              <div style={{ color: '#4B5563', fontSize: '14px', lineHeight: '20px' }}>
                <p>所在地の想定リスクは、国や自治体等が制作しているハザードマップから確認できます。</p>
                <p>SONAE-AIでは公開データに基づいて参考情報を提供していますが、ご自身でもハザードマップで確認いただくことを推奨します。</p>
                <p className="mt-2">【参考】</p>
                <p>
                  ハザードマップポータルサイト{' '}
                  <a
                    href="https://disaportal.gsi.go.jp/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    https://disaportal.gsi.go.jp/
                  </a>
                </p>
                <p>
                  東京都被害想定ホームページ{' '}
                  <a
                    href="https://www.higaisoutei.metro.tokyo.lg.jp/mydmgpred.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    https://www.higaisoutei.metro.tokyo.lg.jp/mydmgpred.html
                  </a>
                </p>
                <p>
                  J-SHIS 地震ハザードステーション{' '}
                  <a
                    href="https://www.j-shis.bosai.go.jp/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    https://www.j-shis.bosai.go.jp/
                  </a>
                </p>
              </div>
            </div>
          ),
          example: <div></div>,
        },
      },
    ],
  },
  {
    id: 'important_business',
    title: '3. 地震発生時の対応',
    fields: [
      {
        id: 'earthquake_response',
        label: '3-1. 地震直後における各自の行動',
        type: 'textarea',
        minHeight: 'medium',
        aiConsult: true,
        defaultValue:
          '【就業時間中】\n① 自身(来客者)の安全確保\n（落下物に注意、EVに乗らない、消火等）\n② (必要に応じて)避難の実施\n（避難情報に留意し、避難場所等に避難）\n③ 家族の安否確認\n④ 会社へ安否報告\n⑤ 安全な場所に留まり指示を待つ\n\n【就業時間外】\n① 自身の安全確保\n② (必要に応じて)避難の実施\n（避難情報に留意し、最寄りの避難所に避難）\n③ 会社へ安否報告、指示を待つ\n④ 安全な場所に留まり指示を待つ',
      },
      {
        id: 'disaster_response_team',
        label: '3-2. 対策本部の立ち上げ(初期・復旧対応）',
        type: 'textarea',
        minHeight: 'small',
        aiConsult: true,
        highlightPlaceholders: true,
        defaultValue:
          '本部長：{役職名を入力}\n\n【初期対応チーム】\n・メンバー：{役職名を入力}\n・役割：安否情報集約、各設備の被害状況集約、従業員の対応計画、負傷者対応\n\n【復旧対応チーム】\n・メンバー：{役職名を入力}\n・役割：重要事業の実施体制検討、システム状況対応、資金繰り管理、各種情報収集（災害情報、協力会社、他社の状況等）、情報発信',
        guide: {
          content: <div></div>,
          example: (
            <div>
              <p className="font-medium mb-3" style={{ color: '#0D357F' }}>記載例</p>
              <div className="space-y-2 p-4 rounded" style={{ backgroundColor: '#FFFFFF' }}>
                <p>本部長：社長</p>
                <p className="mt-2 font-medium">【初期対応チーム】</p>
                <p>・メンバー：総務課長・人事課長・施設管理課長</p>
                <p>・役割：安否情報集約、各設備の被害状況集約、従業員の対応計画、負傷者対応</p>
                <p className="mt-2 font-medium">【復旧対応チーム】</p>
                <p>・メンバー：営業課長・広報課長・情報システム課長</p>
                <p>・役割：重要事業の実施体制検討、システム状況対応、資金繰り管理、各種情報収集（災害情報、協力会社、他社の状況等）、情報発信</p>
              </div>
            </div>
          ),
        },
      },
    ],
  },
  {
    id: 'response_team',
    title: '4. 重要事業情報',
    fields: [
      {
        id: 'priority_business',
        label: '4-1. 重点事業の選定・復旧計画',
        type: 'textarea',
        minHeight: 'medium',
        aiConsult: true,
        placeholder:
          '優先して復旧する重要な事業（重点事業）・優先順位に基づく理由付・目標復旧期間を記載して下さい。',
        guide: {
          content: (
            <div className="space-y-2">
              <p className="font-medium" style={{ color: '#0D357F' }}>重点事業選定・必要な経営資源・現地復旧以外の対応策の考え方</p>

              <p className="font-medium">1.重点事業の選定：</p>
              <p className="ml-4">
                地震によって事業が中断した場合、中断した事業すべてを同時に復旧させるのは、限られた経営資源のなか、現実的ではありません。「売上への貢献度」「顧客への迷惑」「社会全体への迷惑」「顧客離れの可能性」等の観点から、重点事業を選定しましょう。
              </p>

              <p className="font-medium">2.目標復旧時間の設定</p>
              <p className="ml-4">
                事業中断時にどのくらいの期間で重要事業を復旧させるかを定めます。「顧客への迷惑」「社会全体への迷惑」「顧客離れの可能性」「財務影響」に大きな影響が出始める期間を設定します。
              </p>

              <p className="font-medium">3.必要な経営資源の整理：</p>
              <p className="ml-4">
                重点事業を遂行するのに必要な経営資源を「人」「モノ」「資金」「情報」等の観点から入力しましょう。
              </p>

              <p className="font-medium">3.現地復旧以外の対応策：</p>
              <p className="ml-4">
                重点事業の遂行は現地復旧が原則となります。ただし、被災等により現地復旧が困難な場合に備え、「他拠点」「在庫活用」「外部委託」「外部購入」等の観点から、現地復旧以外の対応策も検討しておきましょう。
              </p>
            </div>
          ),
          example: (
            <div>
              <p className="font-medium mb-3" style={{ color: '#0D357F' }}>記載例</p>
              <div className="space-y-2 p-4 rounded" style={{ backgroundColor: '#FFFFFF' }}>
                <p className="font-medium">重要事業①：A製品の製造</p>
                <p className="font-medium">【目標復旧時間】</p>
                <p className="ml-4">・2週間</p>
                <p className="mt-2 font-medium">【現地で復旧する場合に必要な人・モノ・資金・情報 等）】</p>
                <p className="ml-4">・資格保有者・加工機械・原材料・PC(顧客管理システム)・電気・ガス</p>
                <p className="mt-2 font-medium">【現地復旧以外の対応策】</p>
                <p className="ml-4">・在庫活用・横浜商事に生産委託</p>
              </div>
            </div>
          ),
        },
      },
      {
        id: 'shortage_items',
        label: '4-2. 重要事業継続にあたり検討が必要な(不足している)事項',
        type: 'textarea',
        minHeight: 'small',
        aiConsult: true,
        placeholder:
          '重点事業を継続するために不足している要素（人材、設備、資金、情報等）を記載して下さい。',
      },
    ],
  },
  {
    id: 'recovery_plan',
    title: '5-1. 事前に整備・実施する事項',
    displayTitle: '5. 事前準備',
    fields: [
      {
        id: 'checklist',
        label: '5-1. 事前に整備・実施する事項',
        type: 'textarea',
        minHeight: 'small',
        aiConsult: true,
        defaultValue:
          '・備蓄品の整備\n・従業員の安否確認方法の確立・周知\n・重要情報のバックアップ、機器の整備（PC／複合機／電話／非常用電源／テレワーク対応機器等）\n・落下物の防止対策、耐震対策\n・一斉帰宅抑制や家族間の安否確認、自宅での備蓄品整備に係る啓発',
        guide: {
          content: (
            <div className="space-y-2">
              <p className="font-medium" style={{ color: '#0D357F', fontSize: '14px', lineHeight: '20px' }}>補助金／助成金の活用</p>
              <div style={{ color: '#4B5563', fontSize: '14px', lineHeight: '20px' }}>
                <p>備蓄物資の購入に補助金／助成金を活用できる場合があるので、必要に応じて事前に確認しましょう。</p>
                <p>
                  参考ページ：{' '}
                  <a
                    href="https://mirasapo-plus.go.jp/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    https://mirasapo-plus.go.jp/
                  </a>
                </p>
                <p className="mt-2 font-medium" style={{ color: '#4B5563' }}>一斉帰宅抑制・帰宅困難者対策について</p>
                <p>首都直下地震の際は、従業員の一斉帰宅抑制（3日間）が推奨されています。必要に応じてガイドブック等を確認しましょう。</p>
                <p>
                  参考ページ：{' '}
                  <a
                    href="https://www.bousai.metro.tokyo.lg.jp/_res/projects/default_project/_page_/001/001/369/202303.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    https://www.bousai.metro.tokyo.lg.jp/_res/projects/default_project/_page_/001/001/369/202303.pdf
                  </a>
                </p>
              </div>
            </div>
          ),
          example: <div></div>,
        },
      },
    ],
  },
];

export type IntroductoryBcpFieldId =
  (typeof INTRODUCTORY_BCP_SECTIONS)[number]['fields'][number]['id'];
